export type PlaybackIntelligencePolicy = {
  relatedTermsWeight: number;
  sameChannelWeight: number;
  sameCategoryWeight: number;
  personalizedWeight: number;
  publicWeight: number;
  advertisementRelevanceWeight: number;
  recentHistorySize: number;
};

export type PlaybackCandidate = {
  id: string;
  title: string;
  channel?: string;
  category?: string;
  tags?: string[];
  adKeywords?: string[];
  isAdvertisement?: boolean;
  rankingScore?: number;
  ranking?: {
    publicScore?: number;
    personalizedScore?: number;
    userSignals?: {
      clicks?: number;
      watchSeconds?: number;
      favorited?: boolean;
      commented?: boolean;
      tagAffinity?: number;
      skips?: number;
    };
  };
};

export type RankedPlaybackCandidate<T extends PlaybackCandidate = PlaybackCandidate> = {
  video: T;
  score: number;
  probability: number;
  reasons: string[];
};

export type PlaybackHealthSample = {
  stalls: number;
  retries: number;
  failures: number;
  nodeSwitches: number;
  bandwidthBps: number;
  mediaBitrateBps: number;
  cacheRequests: number;
  cacheHits: number;
};

export type PlaybackDiagnosis = {
  severity: "healthy" | "warning" | "critical";
  title: string;
  cause: string;
  action: string;
  cacheHitRate: number;
};

export type IntelligentCacheTarget<T extends PlaybackCandidate = PlaybackCandidate> =
  RankedPlaybackCandidate<T> & {
    quality: "原画" | "1080p" | "720p" | "480p";
    segmentCount: number;
  };

function terms(video: PlaybackCandidate) {
  return new Set(
    [video.title, video.channel, ...(video.tags || []), ...(video.adKeywords || [])]
      .filter(Boolean)
      .join(" ")
      .toLocaleLowerCase()
      .split(/[\s,._\-，。:：/|#]+/)
      .filter((term) => term.length >= 2),
  );
}

export function rankPlaybackCandidates<T extends PlaybackCandidate>(
  items: T[],
  current: T,
  policy: PlaybackIntelligencePolicy,
  recentVideoIds: string[] = [],
): RankedPlaybackCandidate<T>[] {
  const currentTerms = terms(current);
  const recent = new Set(
    policy.recentHistorySize > 0
      ? recentVideoIds.slice(-policy.recentHistorySize)
      : [],
  );
  const available = items.filter((candidate) => candidate.id !== current.id);
  const fresh = available.filter((candidate) => !recent.has(candidate.id));
  const candidates = fresh.length ? fresh : available;
  const scored = candidates.map((video) => {
    const sharedTerms = [...terms(video)].filter((term) => currentTerms.has(term)).length;
    const signals = video.ranking?.userSignals;
    const reasons: string[] = [];
    if (sharedTerms) reasons.push(`${sharedTerms} 个相关关键词`);
    if (video.channel && video.channel === current.channel) reasons.push("同频道偏好");
    if (video.category && video.category === current.category) reasons.push("同分类兴趣");
    if (signals?.favorited) reasons.push("已收藏");
    if ((signals?.watchSeconds || 0) >= 30) reasons.push("历史观看较长");
    if ((signals?.tagAffinity || 0) > 0) reasons.push("搜索/标签匹配");
    if ((signals?.skips || 0) > 0) reasons.push("已降低跳过内容权重");
    if (!reasons.length) reasons.push("公共热度");

    const signalScore =
      Math.min(2, (signals?.clicks || 0) * 0.08) +
      Math.min(3, (signals?.watchSeconds || 0) / 300) +
      (signals?.favorited ? 1.5 : 0) +
      (signals?.commented ? 0.7 : 0) +
      Math.min(2, Math.max(0, signals?.tagAffinity || 0) / 10);
    const positiveScore =
      sharedTerms * (policy.relatedTermsWeight / 10) +
      (video.isAdvertisement
        ? sharedTerms * (policy.advertisementRelevanceWeight / 10)
        : 0) +
      (video.channel === current.channel ? policy.sameChannelWeight / 10 : 0) +
      (video.category === current.category ? policy.sameCategoryWeight / 10 : 0) +
      ((video.ranking?.personalizedScore || 0) / 100) * policy.personalizedWeight +
      ((video.ranking?.publicScore || video.rankingScore || 0) / 100) * policy.publicWeight +
      signalScore;
    const score = positiveScore * Math.max(0.35, 1 - (signals?.skips || 0) * 0.15);
    return { video, score, probability: 0, reasons };
  });
  if (!scored.length) return [];
  const max = Math.max(...scored.map((item) => item.score));
  const exponentials = scored.map((item) => Math.exp(Math.min(20, item.score - max)));
  const total = exponentials.reduce((sum, value) => sum + value, 0) || 1;
  return scored
    .map((item, index) => ({ ...item, probability: exponentials[index] / total }))
    .sort((left, right) => right.score - left.score);
}

export function diagnosePlayback(sample: PlaybackHealthSample): PlaybackDiagnosis {
  const cacheHitRate = sample.cacheRequests > 0
    ? sample.cacheHits / sample.cacheRequests
    : 0;
  const bandwidthRatio = sample.mediaBitrateBps > 0
    ? sample.bandwidthBps / sample.mediaBitrateBps
    : Number.POSITIVE_INFINITY;
  if (sample.failures > 0) {
    return {
      severity: "critical",
      title: "播放源或节点异常",
      cause: `发生 ${sample.failures} 次不可恢复错误，节点切换 ${sample.nodeSwitches} 次。`,
      action: "检查失败分片、源站响应和节点健康；保留低一档画质作为回退。",
      cacheHitRate,
    };
  }
  if (sample.stalls > 0 && bandwidthRatio < 1.2) {
    return {
      severity: "warning",
      title: "带宽低于当前画质需求",
      cause: `实测带宽仅为媒体码率的 ${Math.max(0, bandwidthRatio).toFixed(1)} 倍，已卡顿 ${sample.stalls} 次。`,
      action: "优先缓存当前画质分片，并在持续不足时降一档画质或切换更快节点。",
      cacheHitRate,
    };
  }
  if (sample.stalls > 0 && sample.cacheRequests >= 3 && cacheHitRate < 0.25) {
    return {
      severity: "warning",
      title: "本地缓存偏冷",
      cause: `缓存命中率仅 ${Math.round(cacheHitRate * 100)}%，多数分片仍从网络读取。`,
      action: "提高高概率推荐视频的开头分片预缓存，避免把整部低概率视频提前下载。",
      cacheHitRate,
    };
  }
  if (sample.stalls > 0 || sample.retries > 0) {
    return {
      severity: "warning",
      title: "存在短时播放抖动",
      cause: `检测到 ${sample.stalls} 次卡顿、${sample.retries} 次分片重试，当前带宽总体足够。`,
      action: "继续观察浏览器解码负载、分片时间戳和瞬时节点延迟。",
      cacheHitRate,
    };
  }
  return {
    severity: "healthy",
    title: "播放正常",
    cause: sample.cacheRequests
      ? `当前未发现卡顿，缓存命中率 ${Math.round(cacheHitRate * 100)}%。`
      : "正在收集播放分片、带宽和缓存数据。",
    action: "保持当前节点和画质，继续按推荐概率预缓存。",
    cacheHitRate,
  };
}

export function buildIntelligentCachePlan<T extends PlaybackCandidate>(
  ranked: RankedPlaybackCandidate<T>[],
  requested: number,
  bandwidthBps: number,
  saveData = false,
): IntelligentCacheTarget<T>[] {
  const count = Math.max(0, Math.min(ranked.length, Math.floor(requested)));
  const quality = saveData || bandwidthBps < 2_500_000
    ? "480p"
    : bandwidthBps < 5_000_000
      ? "720p"
      : bandwidthBps < 12_000_000
        ? "1080p"
        : "原画";
  return ranked.slice(0, count).map((candidate, index) => ({
    ...candidate,
    quality,
    segmentCount: Math.max(2, Math.min(8, 6 - index)),
  }));
}
